/**
 * @file utils/ui/fullscreenSession.ts
 * @description Full screen from calibration to the last task, and a record of how it held.
 *
 * Full screen fixes the geometry the gaze model is trained on: window = screen, no browser
 * bars, the same text line breaks for everyone on the same screen. Leaving it (Esc) is
 * detected so the app can ask the participant to return, and every exit is recorded.
 * Browsers only allow entering full screen from a click, so `requestAssessmentFullscreen`
 * must be called directly inside a click handler.
 */

export const FULLSCREEN_SETTINGS = {
  version: 'fullscreen-1',
  /** Chrome reports the old window size at the moment of switching; read it after this delay. */
  settleMs: 400,
} as const;

export interface FullscreenRecord {
  version: typeof FULLSCREEN_SETTINGS.version;
  /** The browser offers the Fullscreen API. */
  supported: boolean;
  /** Full screen was requested (the participant clicked the start button). */
  requested: boolean;
  /** Full screen was actually reached at least once. */
  entered: boolean;
  /** Times the participant left full screen after entering it. */
  exits: number;
  /** Total time spent outside full screen after first entering it. */
  msOutside: number;
  /** Window size once full screen had settled (CSS px). */
  viewport: { width: number; height: number } | null;
  screen: { width: number; height: number };
  /** Differs from the display's native ratio when the browser is zoomed. */
  devicePixelRatio: number;
}

type FullscreenDocument = Document & { webkitFullscreenElement?: Element | null };
type FullscreenElement = HTMLElement & { webkitRequestFullscreen?: () => void };

const state = {
  requested: false,
  entered: false,
  exits: 0,
  msOutside: 0,
  leftAt: null as number | null,
  viewport: null as { width: number; height: number } | null,
  listening: false,
};
const listeners = new Set<() => void>();

export const isFullscreenSupported = (): boolean => {
  const el = document.documentElement as FullscreenElement;
  return typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function';
};

export const isFullscreenActive = (): boolean => {
  const doc = document as FullscreenDocument;
  return !!(doc.fullscreenElement ?? doc.webkitFullscreenElement);
};

const onChange = (): void => {
  const now = Date.now();
  if (isFullscreenActive()) {
    if (state.leftAt !== null) state.msOutside += now - state.leftAt;
    state.leftAt = null;
    state.entered = true;
    setTimeout(() => {
      if (isFullscreenActive()) state.viewport = { width: window.innerWidth, height: window.innerHeight };
    }, FULLSCREEN_SETTINGS.settleMs);
  } else if (state.entered && state.leftAt === null) {
    state.exits++;
    state.leftAt = now;
  }
  listeners.forEach((listener) => listener());
};

const listen = (): void => {
  if (state.listening) return;
  state.listening = true;
  document.addEventListener('fullscreenchange', onChange);
  document.addEventListener('webkitfullscreenchange', onChange);
};

/** Enter full screen. Call directly inside a click handler; resolves false when refused or unsupported. */
export const requestAssessmentFullscreen = async (): Promise<boolean> => {
  listen();
  state.requested = true;
  const el = document.documentElement as FullscreenElement;
  try {
    if (typeof el.requestFullscreen === 'function') await el.requestFullscreen();
    else if (typeof el.webkitRequestFullscreen === 'function') el.webkitRequestFullscreen();
    else return false;
    return true;
  } catch {
    return false;
  }
};

/** Notified on every full-screen change (for the return prompt). Returns an unsubscribe function. */
export const onFullscreenChange = (listener: () => void): (() => void) => {
  listen();
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** True once full screen was reached in this session and the participant has since left it. */
export const hasLeftFullscreen = (): boolean => state.entered && !isFullscreenActive();

export const getFullscreenRecord = (): FullscreenRecord => ({
  version: FULLSCREEN_SETTINGS.version,
  supported: isFullscreenSupported(),
  requested: state.requested,
  entered: state.entered,
  exits: state.exits,
  msOutside: state.msOutside + (state.leftAt !== null ? Date.now() - state.leftAt : 0),
  viewport: state.viewport,
  screen: { width: window.screen.width, height: window.screen.height },
  devicePixelRatio: window.devicePixelRatio,
});

export const resetFullscreenRecord = (): void => {
  state.requested = false;
  state.entered = false;
  state.exits = 0;
  state.msOutside = 0;
  state.leftAt = null;
  state.viewport = null;
};
